/* NOTE: Updated to avoid using Cloudinary 'eager' parameter for UNSIGNED uploads.
   Cloudinary disallows eager on unsigned presets. We introduce a `signed` flag; only
   when signed=true will eager transformations be requested. All other optimization
   still happens via on-the-fly delivery transforms (buildTransformedUrl + f_auto/q_auto).
*/

import axios from 'axios';

// ==================== Default Sets ====================
export const DEFAULT_IMAGE_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/avif',
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
]);
export const DEFAULT_VIDEO_MIME_TYPES = new Set([
  'video/mp4',
  'video/m4v',
  'video/webm',
  'video/ogg',
  'video/ogv',
  'video/quicktime',
  'video/x-flv',
  'video/3gpp',
  'video/3gpp2',
  'video/x-msvideo',
  'video/x-matroska',
  'application/octet-stream',
]);

const defaultIsVideoName = (n = '') =>
  /\.(mp4|m4v|webm|ogg|ogv|mov|flv|3gp|3g2|avi|mkv)$/i.test(n);
const defaultIsImageName = (n = '') =>
  /\.(jpe?g|png|gif|webp|avif|heic|heif|bmp|tiff|svg)$/i.test(n);

// ==================== Utils ====================
export const formatBytes = (b, d = 2) => {
  if (!b) return '0 B';
  const k = 1024;
  const i = Math.floor(Math.log(b) / Math.log(k));
  return `${parseFloat((b / Math.pow(k, i)).toFixed(d))} ${
    ['B', 'KB', 'MB', 'GB', 'TB'][i]
  }`;
};
export const isSupportedFileType = (
  file,
  {
    imageMimeTypes = DEFAULT_IMAGE_MIME_TYPES,
    videoMimeTypes = DEFAULT_VIDEO_MIME_TYPES,
    isImageName = defaultIsImageName,
    isVideoName = defaultIsVideoName,
  } = {},
) => {
  if (!file) return false;
  const { type, name = '' } = file;
  if (imageMimeTypes.has(type) || videoMimeTypes.has(type)) return true;
  const l = name.toLowerCase();
  return isImageName(l) || isVideoName(l);
};
export const computeFileLimit = (
  file,
  {
    maxImageBytes = 40 * 1024 * 1024,
    maxVideoBytes = 500 * 1024 * 1024,
    isVideoName = defaultIsVideoName,
  } = {},
) => {
  if (!file) return 0;
  return isVideoName(file.name) ? maxVideoBytes : maxImageBytes;
};

// ==================== Errors ====================
export class MediaUploadError extends Error {
  constructor(code, message, meta = {}) {
    super(message);
    this.name = 'MediaUploadError';
    this.code = code;
    this.meta = meta;
  }
}
const err = (c, m, meta) => new MediaUploadError(c, m, meta);
export const ERROR_CODES = {
  NO_FILE: 'NO_FILE',
  TYPE_UNSUPPORTED: 'TYPE_UNSUPPORTED',
  TOO_LARGE: 'TOO_LARGE',
  DIMENSIONS_EXCEED: 'DIMENSIONS_EXCEED',
  MEGAPIXELS_EXCEED: 'MEGAPIXELS_EXCEED',
  VIDEO_META_UNREADABLE: 'VIDEO_META_UNREADABLE',
  VIDEO_DURATION_EXCEED: 'VIDEO_DURATION_EXCEED',
  VIDEO_BITRATE_EXCEED: 'VIDEO_BITRATE_EXCEED',
  UPLOAD_CANCELED: 'UPLOAD_CANCELED',
  CHUNK_FAILED: 'CHUNK_FAILED',
  DIRECT_FAILED: 'DIRECT_FAILED',
  RESUME_STATE_CORRUPT: 'RESUME_STATE_CORRUPT',
  DEDUP_MATCH: 'DEDUP_MATCH',
  HASH_FAILED: 'HASH_FAILED',
  INTERNAL: 'INTERNAL',
};

// ==================== IndexedDB (resume + dedupe) ====================
const DB_NAME = 'mediaUploaderDB',
  DB_VERSION = 1,
  STORE_RESUMES = 'resumables',
  STORE_DEDUPE = 'dedupe';
let dbPromise = null;
let storageWarningLogged = false;
function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((res, rej) => {
    if (!('indexedDB' in window)) {
      res(null);
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_RESUMES))
        db.createObjectStore(STORE_RESUMES, { keyPath: 'token' });
      if (!db.objectStoreNames.contains(STORE_DEDUPE))
        db.createObjectStore(STORE_DEDUPE, { keyPath: 'fingerprint' });
    };
    req.onsuccess = () => res(req.result);
    req.onerror = () => rej(req.error);
  });
  return dbPromise;
}

function warnStorageFailure(operation, error) {
  if (storageWarningLogged) return;
  storageWarningLogged = true;
  console.warn('[mediaUpload] upload cache unavailable; continuing without resume/dedupe', {
    operation,
    message: error?.message || String(error || ''),
  });
}
// Show a toast or modal when IndexedDB is closed
export function handleUploadError(e) {
  if (
    e instanceof MediaUploadError &&
    e.code === 'INTERNAL' &&
    /database connection is closing|database is closed/i.test(e.message)
  ) {
    // Show a friendly message
    alert(
      'Your browser was offline or asleep for a while. Please refresh the page and try your upload again!',
    );
    return;
  }
  // Fallback: show generic error
  alert(e.message || 'Upload failed. Please try again.');
}
async function withStore(store, mode, fn) {
  const db = await openDB();
  if (!db || db.closePending || db.closed) return null; // Defensive: skip if closing/closed
  return new Promise((res, rej) => {
    let tx;
    try {
      tx = db.transaction(store, mode);
    } catch (e) {
      // Database is closing or closed
      res(null);
      return;
    }
    const s = tx.objectStore(store);
    let out;
    try {
      out = fn(s);
    } catch (e) {
      rej(e);
      return;
    }
    tx.oncomplete = () => res(out);
    tx.onerror = () => rej(tx.error);
  });
}

async function withBestEffortStore(operation, fallback, run) {
  try {
    const result = await run();
    return result ?? fallback;
  } catch (error) {
    warnStorageFailure(operation, error);
    return fallback;
  }
}

const saveResumeState = (r) =>
  withBestEffortStore('saveResumeState', null, () =>
    withStore(STORE_RESUMES, 'readwrite', (s) => s.put(r)),
  );
const loadResumeState = (t) =>
  withBestEffortStore('loadResumeState', null, () =>
    withStore(STORE_RESUMES, 'readonly', (s) => s.get(t)),
  );
const deleteResumeState = (t) =>
  withBestEffortStore('deleteResumeState', null, () =>
    withStore(STORE_RESUMES, 'readwrite', (s) => s.delete(t)),
  );
const listResumeStates = () =>
  withBestEffortStore('listResumeStates', [], () =>
    withStore(STORE_RESUMES, 'readonly', (s) => {
      const req = s.getAll();
      return new Promise((res, rej) => {
        req.onsuccess = () => res(req.result);
        req.onerror = () => rej(req.error);
      });
    }),
  );
const saveDedupeRecord = (r) =>
  withBestEffortStore('saveDedupeRecord', null, () =>
    withStore(STORE_DEDUPE, 'readwrite', (s) => s.put(r)),
  );
const loadDedupeRecord = (f) =>
  withBestEffortStore('loadDedupeRecord', null, () =>
    withStore(STORE_DEDUPE, 'readonly', (s) => s.get(f)),
  );

// ==================== Hash / Fingerprint ====================
export const computeFingerprint = (file) =>
  [file.name, file.type, file.size, file.lastModified].join('|');
async function computePartialSHA1(file) {
  try {
    const first = file.slice(0, Math.min(1024 * 1024, file.size));
    const last = file.slice(
      Math.max(0, file.size - Math.min(1024 * 1024, file.size)),
      file.size,
    );
    const buf = await new Blob([first, last]).arrayBuffer();
    const hash = await crypto.subtle.digest('SHA-1', buf);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    throw err(ERROR_CODES.HASH_FAILED, 'Failed to hash file.');
  }
}

// ==================== Gates ====================
async function inspectImageDimensions(
  file,
  { maxMegapixels, maxWidth, maxHeight },
) {
  if (!file.type.startsWith('image/')) return null;
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    const meta = await new Promise((res, rej) => {
      img.onload = () =>
        res({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () =>
        rej(
          err(
            ERROR_CODES.DIMENSIONS_EXCEED,
            'Could not read image dimensions.',
          ),
        );
      img.src = url;
    });
    const { width, height } = meta;
    const mp = (width * height) / 1e6;
    if (maxWidth && width > maxWidth)
      throw err(
        ERROR_CODES.DIMENSIONS_EXCEED,
        `Width ${width}px exceeds ${maxWidth}px.`,
      );
    if (maxHeight && height > maxHeight)
      throw err(
        ERROR_CODES.DIMENSIONS_EXCEED,
        `Height ${height}px exceeds ${maxHeight}px.`,
      );
    if (maxMegapixels && mp > maxMegapixels)
      throw err(
        ERROR_CODES.MEGAPIXELS_EXCEED,
        `Image ${mp.toFixed(1)}MP exceeds ${maxMegapixels}MP limit.`,
      );
    return { width, height, megapixels: mp };
  } finally {
    URL.revokeObjectURL(url);
  }
}
async function inspectVideoMeta(file, { maxDurationSec, maxBitrateMbps }) {
  if (!file.type.startsWith('video/') && !defaultIsVideoName(file.name))
    return null;
  const url = URL.createObjectURL(file);
  try {
    const video = document.createElement('video');
    video.preload = 'metadata';
    const meta = await new Promise((res, rej) => {
      video.onloadedmetadata = () =>
        res({
          duration: video.duration,
          width: video.videoWidth,
          height: video.videoHeight,
        });
      video.onerror = () =>
        rej(
          err(ERROR_CODES.VIDEO_META_UNREADABLE, 'Cannot read video metadata.'),
        );
      video.src = url;
    });
    const { duration } = meta;
    if (maxDurationSec && duration > maxDurationSec)
      throw err(
        ERROR_CODES.VIDEO_DURATION_EXCEED,
        `Video duration ${duration.toFixed(1)}s exceeds ${maxDurationSec}s.`,
      );
    const bitrateMbps = (file.size * 8) / duration / 1_000_000;
    if (maxBitrateMbps && bitrateMbps > maxBitrateMbps)
      throw err(
        ERROR_CODES.VIDEO_BITRATE_EXCEED,
        `Bitrate ${bitrateMbps.toFixed(1)}Mbps exceeds ${maxBitrateMbps}Mbps.`,
      );
    return { ...meta, bitrateMbps };
  } finally {
    URL.revokeObjectURL(url);
  }
}

// ==================== Thumbnails ====================
async function extractVideoThumbnails(file, times = [0, 0.5]) {
  if (!file.type.startsWith('video/') && !defaultIsVideoName(file.name))
    return [];
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.preload = 'metadata';
  video.src = url;
  await new Promise((res, rej) => {
    video.onloadedmetadata = () => res();
    video.onerror = () => rej();
  });
  const duration = video.duration;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const thumbs = [];
  for (const ratio of times) {
    const t = Math.min(duration * ratio, Math.max(0, duration - 0.05));
    video.currentTime = t;
    await new Promise((res) => {
      const h = () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        thumbs.push({ time: t, dataUrl: canvas.toDataURL('image/jpeg', 0.7) });
        video.removeEventListener('seeked', h);
        res();
      };
      video.addEventListener('seeked', h);
    });
  }
  URL.revokeObjectURL(url);
  return thumbs;
}

// ==================== Progress Smoothing ====================
function createProgressSmoother(onProgress, { throttleMs = 120 } = {}) {
  let lastTs = 0,
    lastRaw = 0,
    anim = false,
    target = 0,
    current = 0;
  const step = () => {
    const d = target - current;
    if (Math.abs(d) < 0.5) {
      current = target;
      onProgress(Math.round(current));
      anim = false;
      return;
    }
    current += Math.max(0.5, d * 0.15);
    onProgress(Math.min(100, Math.round(current)));
    requestAnimationFrame(step);
  };
  return (pct) => {
    const now = performance.now();
    if (pct < 100 && now - lastTs < throttleMs && pct - lastRaw < 2) return;
    lastTs = now;
    lastRaw = pct;
    target = pct;
    if (!anim) {
      anim = true;
      requestAnimationFrame(step);
    }
  };
}

// ==================== Factory ====================
export function createMediaUploader({
  cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD || 'joegalaxy',
  uploadPreset = process.env.REACT_APP_CLOUDINARY_PRESET || 'cubejg',
  axiosInstance = axios,
  imageMimeTypes = DEFAULT_IMAGE_MIME_TYPES,
  videoMimeTypes = DEFAULT_VIDEO_MIME_TYPES,
  maxImageBytes = 50 * 1024 * 1024,
  maxVideoBytes = 512 * 1024 * 1024,
  chunkThresholdBytes = 90 * 1024 * 1024,
  chunkSizeBytes = 20 * 1024 * 1024,
  transformImage = 'q_auto,fl_progressive:steep',
  transformVideo = 'f_auto,q_auto,vc_auto',
  eagerVideoLadder = [
    { transformation: 'q_auto:eco,vc_auto,w_640', format: 'mp4' },
    { transformation: 'q_auto:good,vc_auto,w_960', format: 'mp4' },
    { transformation: 'q_auto:good,vc_auto,w_1280', format: 'mp4' },
  ],
  eagerGifOptimize = [
    { transformation: 'f_mp4,q_auto', format: 'mp4' },
    { transformation: 'f_webm,q_auto', format: 'webm' },
  ],
  isVideoName = defaultIsVideoName,
  isImageName = defaultIsImageName,
  // Modern phones (e.g., 108MP sensors) can exceed 12k px; relax caps to allow panoramas
  maxImageMegapixels = 150,
  maxImageWidth = 16000,
  maxImageHeight = 16000,
  maxVideoDurationSec = 20 * 60,
  maxVideoBitrateMbps = 60,
  enableVideoMetaCheck = true,
  enableImageDimensionCheck = true,
  enableHashDedupe = true,
  enableThumbnails = true,
  thumbnailTimes = [0, 0.5],
  enableResume = true,
  enableGifOptimization = true,
  enableAdaptiveLadder = true,
  enableFallbackVariants = true,
  fallbackFormats = ['webp', 'jpg'],
  debug = false,
  forceAutoResourceType = true,
  signed = false, // NEW: when false (unsigned preset) we disable eager transformations
} = {}) {
  const API_BASE = `https://api.cloudinary.com/v1_1/${cloudName}`;
  let abortCtrl = null,
    canceled = false;

  const logDebug = (...a) => {
    if (debug) console.log('[mediaUpload]', ...a);
  };

  const buildTransformedUrl = (url, video) => {
    if (!url || !url.includes('/upload/')) return url;
    const needed = (video ? transformVideo : transformImage)
      .split(',')
      .filter(Boolean);
    const hasAll = needed.every(
      (p) =>
        url.includes(`/${p}/`) ||
        url.includes(`/${p},`) ||
        url.includes(`,${p},`) ||
        url.includes(`,${p}/`),
    );
    if (hasAll) return url;
    return url.replace('/upload/', `/upload/${needed.join(',')}/`);
  };

  const applyFallbackVariants = (secureUrl) => {
    if (!enableFallbackVariants || !secureUrl.includes('/upload/')) return [];
    const [a, b] = secureUrl.split('/upload/');
    return fallbackFormats.map((fmt) => {
      const variant = `${a}/upload/f_${fmt},${b}`.replace(
        /(f_\w+,)+/,
        'f_' + fmt + ',',
      );
      return { format: fmt, url: variant };
    });
  };

  // Eager disabled for unsigned uploads
  const buildEagerParam = (isGif, isVid) => {
    if (!signed) return null; // ONLY allow when signed
    const entries = [];
    if (enableGifOptimization && isGif) entries.push(...eagerGifOptimize);
    if (enableAdaptiveLadder && isVid) entries.push(...eagerVideoLadder);
    if (!entries.length) return null;
    return entries
      .map((e) =>
        `${e.transformation}${e.format ? `/${e.format}` : ''}`.replace(
          /\/+/g,
          '/',
        ),
      )
      .join('|');
  };

  const validate = (file) => {
    if (!file) throw err(ERROR_CODES.NO_FILE, 'No file provided.');
    if (
      !isSupportedFileType(file, {
        imageMimeTypes,
        videoMimeTypes,
        isVideoName,
        isImageName,
      })
    )
      throw err(
        ERROR_CODES.TYPE_UNSUPPORTED,
        `${file.name} format not supported.`,
      );
    const limit = computeFileLimit(file, {
      maxImageBytes,
      maxVideoBytes,
      isVideoName,
    });
    if (file.size > limit)
      throw err(
        ERROR_CODES.TOO_LARGE,
        `${file.name} too large (max ${formatBytes(limit)}).`,
        { provided: file.size, limit },
      );
    return {
      validatedFile: file,
      isVideo: isVideoName(file.name),
      isImage: isImageName(file.name),
      maxBytes: limit,
    };
  };

  const cancel = () => {
    canceled = true;
    if (abortCtrl) abortCtrl.abort();
  };

  const enhanceCloudinaryError = (e, endpoint) => {
    const payload = e.response?.data;
    if (payload && debug) {
      logDebug('Cloudinary error', { endpoint, payload });
      const msg = payload.error?.message || '';
      if (/unsigned upload not allowed/i.test(msg))
        logDebug('Hint: preset not unsigned or needs signing.');
      if (/resource type/i.test(msg))
        logDebug(
          'Hint: using /auto/upload may help; ensure preset resource_type=Auto.',
        );
      if (/eager parameter is not allowed/i.test(msg))
        logDebug('Hint: remove eager for unsigned (done automatically now).');
    }
  };

  const resolveEndpoint = (file, forceVideo) => {
    if (forceAutoResourceType) return `${API_BASE}/auto/upload`;
    if (forceVideo || isVideoName(file.name)) return `${API_BASE}/video/upload`;
    return `${API_BASE}/image/upload`;
  };

  const inspectUploadMetadata = async ({ validatedFile, isVideo, isImage }) => {
    let imageMeta = null;
    if (enableImageDimensionCheck && isImage) {
      imageMeta = await inspectImageDimensions(validatedFile, {
        maxMegapixels: maxImageMegapixels,
        maxWidth: maxImageWidth,
        maxHeight: maxImageHeight,
      });
    }
    let videoMeta = null;
    if (enableVideoMetaCheck && isVideo) {
      videoMeta = await inspectVideoMeta(validatedFile, {
        maxDurationSec: maxVideoDurationSec,
        maxBitrateMbps: maxVideoBitrateMbps,
      });
    }
    return { imageMeta, videoMeta };
  };

  const preflight = async (file) => {
    const validation = validate(file);
    const metadata = await inspectUploadMetadata(validation);
    return {
      ...validation,
      ...metadata,
    };
  };

  const directUpload = async (file, publicId, smoothProgress) => {
    const data = new FormData();
    data.append('file', file);
    data.append('upload_preset', uploadPreset);
    data.append('cloud_name', cloudName);
    data.append('public_id', publicId);
    // NO eager append for unsigned
    abortCtrl = new AbortController();
    let endpoint = resolveEndpoint(file);
    let usedAuto = endpoint.includes('/auto/');
    try {
      logDebug('Direct upload start', {
        endpoint,
        size: file.size,
        name: file.name,
        signed,
      });
      const res = await axiosInstance.post(endpoint, data, {
        signal: abortCtrl.signal,
        onUploadProgress: (e) => {
          if (!e.total) return;
          smoothProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
        },
      });
      return res.data;
    } catch (e) {
      if (canceled) throw err(ERROR_CODES.UPLOAD_CANCELED, 'Upload canceled.');
      enhanceCloudinaryError(e, endpoint);
      const msg = e.response?.data?.error?.message || '';
      if (
        !usedAuto &&
        /resource type|video unsupported|auto.*resource/i.test(msg)
      ) {
        endpoint = `${API_BASE}/auto/upload`;
        usedAuto = true;
        logDebug('Retry direct via /auto/upload');
        try {
          const r2 = await axiosInstance.post(endpoint, data, {
            signal: abortCtrl.signal,
            onUploadProgress: (ev) => {
              if (!ev.total) return;
              smoothProgress(
                Math.min(100, Math.round((ev.loaded / ev.total) * 100)),
              );
            },
          });
          return r2.data;
        } catch (e2) {
          enhanceCloudinaryError(e2, endpoint);
          throw err(ERROR_CODES.DIRECT_FAILED, 'Direct upload failed.', {
            cause: e2,
            cloudinary: e2.response?.data,
          });
        }
      }
      throw err(ERROR_CODES.DIRECT_FAILED, 'Direct upload failed.', {
        cause: e,
        cloudinary: e.response?.data,
      });
    }
  };

  const chunkedUpload = async (
    file,
    publicId,
    smoothProgress,
    resumeRecord,
  ) => {
    abortCtrl = new AbortController();
    const total = file.size;
    const uid =
      resumeRecord?.uid ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    let start = resumeRecord?.lastByte || 0;
    let uploaded = start;
    let lastData = null;
    let baseEndpoint = resolveEndpoint(file, true);
    let switched = baseEndpoint.includes('/auto/');
    while (start < total && !canceled) {
      const end = Math.min(start + chunkSizeBytes, total);
      const chunk = file.slice(start, end);
      const form = new FormData();
      form.append('file', chunk);
      form.append('cloud_name', cloudName);
      form.append('upload_preset', uploadPreset);
      form.append('public_id', publicId);
      // NO eager for chunked unsigned
      const headers = {
        'X-Unique-Upload-Id': uid,
        'Content-Range': `bytes ${start}-${end - 1}/${total}`,
      };
      try {
        logDebug('Chunk upload', { endpoint: baseEndpoint, start, end, total });
        const r = await axiosInstance.post(baseEndpoint, form, {
          headers,
          signal: abortCtrl.signal,
          onUploadProgress: (e) => {
            if (e.loaded) {
              const overall = uploaded + e.loaded;
              smoothProgress(
                Math.min(100, Math.round((overall / total) * 100)),
              );
            }
          },
        });
        lastData = r.data;
      } catch (e) {
        if (canceled)
          throw err(
            ERROR_CODES.UPLOAD_CANCELED,
            'Upload canceled during chunk.',
          );
        enhanceCloudinaryError(e, baseEndpoint);
        const msg = e.response?.data?.error?.message || '';
        if (
          !switched &&
          /resource type|video unsupported|auto.*resource/i.test(msg)
        ) {
          const autoEp = `${API_BASE}/auto/upload`;
          logDebug('Retry chunk via /auto/upload', { start, end });
          try {
            const r2 = await axiosInstance.post(autoEp, form, {
              headers,
              signal: abortCtrl.signal,
              onUploadProgress: (e2) => {
                if (e2.loaded) {
                  const overall = uploaded + e2.loaded;
                  smoothProgress(
                    Math.min(100, Math.round((overall / total) * 100)),
                  );
                }
              },
            });
            lastData = r2.data;
            baseEndpoint = autoEp;
            switched = true;
          } catch (e2) {
            if (enableResume)
              await saveResumeState({
                token: resumeRecord?.token || uid,
                uid,
                publicId,
                lastByte: uploaded,
                fingerprint: resumeRecord?.fingerprint,
                fileSize: file.size,
                fileName: file.name,
                createdAt: Date.now(),
              });
            throw err(ERROR_CODES.CHUNK_FAILED, 'Chunk upload failed.', {
              start,
              end,
              cause: e2,
              cloudinary: e2.response?.data,
            });
          }
        } else {
          if (enableResume)
            await saveResumeState({
              token: resumeRecord?.token || uid,
              uid,
              publicId,
              lastByte: uploaded,
              fingerprint: resumeRecord?.fingerprint,
              fileSize: file.size,
              fileName: file.name,
              createdAt: Date.now(),
            });
          throw err(ERROR_CODES.CHUNK_FAILED, 'Chunk upload failed.', {
            start,
            end,
            cause: e,
            cloudinary: e.response?.data,
          });
        }
      }
      uploaded += end - start;
      start = end;
      if (enableResume)
        await saveResumeState({
          token: resumeRecord?.token || uid,
          uid,
          publicId,
          lastByte: uploaded,
          fingerprint: resumeRecord?.fingerprint,
          fileSize: file.size,
          fileName: file.name,
          createdAt: Date.now(),
        });
    }
    if (canceled) throw err(ERROR_CODES.UPLOAD_CANCELED, 'Upload canceled.');
    if (!lastData?.secure_url)
      throw err(
        ERROR_CODES.CHUNK_FAILED,
        'Chunked upload failed finalization.',
      );
    if (enableResume) await deleteResumeState(resumeRecord?.token || uid);
    return lastData;
  };

  // Public resume helpers
  const listResumables = async () => (await listResumeStates()) || [];
  const purgeResumable = async (t) => {
    if (enableResume) await deleteResumeState(t);
  };
  const purgeAllResumables = async () => {
    const all = await listResumables();
    await Promise.all(all.map((r) => deleteResumeState(r.token)));
  };
  const getCachedUpload = async (fp) => (await loadDedupeRecord(fp)) || null;

  const upload = async (
    file,
    {
      onProgress = () => {},
      publicIdPrefix = 'Uploads',
      forceDirect = false,
      transform = true,
      returnRaw = false,
      dedupe = true,
      resumeToken = null,
    } = {},
  ) => {
    canceled = false;
    const { validatedFile, isVideo, imageMeta, videoMeta } =
      await preflight(file);

    const fingerprint = computeFingerprint(validatedFile);
    let partialHash = null;
    if (enableHashDedupe && dedupe) {
      try {
        partialHash = await computePartialSHA1(validatedFile);
        const cached = await getCachedUpload(fingerprint);
        if (cached && cached.partialHash === partialHash) {
          return {
            secureUrl: cached.secureUrl,
            transformedUrl: transform
              ? buildTransformedUrl(cached.secureUrl, isVideo)
              : cached.secureUrl,
            canceled: false,
            deduped: true,
            imageMeta,
            videoMeta,
            resumeToken: null,
            thumbnails: [],
            variants: cached.variants || [],
            raw: returnRaw ? { cached } : undefined,
          };
        }
      } catch (error) {
        partialHash = null;
        warnStorageFailure('computePartialSHA1', error);
      }
    }

    let thumbnails = [];
    if (enableThumbnails && isVideo) {
      try {
        thumbnails = await extractVideoThumbnails(
          validatedFile,
          thumbnailTimes,
        );
      } catch {}
    }

    const isGif = /\.gif$/i.test(validatedFile.name);
    const extMatch = validatedFile.name.match(/\.[a-z0-9]+$/i);
    const ext = extMatch ? extMatch[0].toLowerCase() : isVideo ? '.mp4' : '';
    const sanitized = validatedFile.name
      .replace(/\s+/g, '_')
      .replace(/\.[a-z0-9]+$/i, '');
    const publicId = `${publicIdPrefix}/${Date.now()}-${sanitized}${ext}`;

    // Since unsigned => eager disabled, buildEagerParam returns null unless signed=true
    const eager = null; // buildEagerParam(isGif,isVideo) intentionally not used
    const extraForm = { eager }; // kept for future signed extension

    let resumeRecord = null;
    if (enableResume) {
      if (resumeToken) {
        const stored = await loadResumeState(resumeToken);
        if (
          stored &&
          stored.fingerprint === fingerprint &&
          stored.fileSize === validatedFile.size
        )
          resumeRecord = stored;
        else if (stored) await deleteResumeState(resumeToken);
      } else {
        const all = await listResumables();
        resumeRecord = all.find(
          (r) =>
            r.fingerprint === fingerprint && r.fileSize === validatedFile.size,
        );
      }
    }

    const smoothProgress = createProgressSmoother(onProgress);
    let data;
    const shouldChunk =
      isVideo && validatedFile.size > chunkThresholdBytes && !forceDirect;

    try {
      data = shouldChunk
        ? await chunkedUpload(
            validatedFile,
            publicId,
            smoothProgress,
            resumeRecord,
          )
        : await directUpload(validatedFile, publicId, smoothProgress);
    } catch (e) {
      // If direct upload fails (e.g., resource-type mismatch) and we haven't chunked yet,
      // retry once via chunked upload to increase robustness for edge presets/endpoints.
      const isDirectFailure =
        e instanceof MediaUploadError && e.code === ERROR_CODES.DIRECT_FAILED;
      if (!shouldChunk && !forceDirect && isDirectFailure) {
        try {
          data = await chunkedUpload(
            validatedFile,
            publicId,
            smoothProgress,
            resumeRecord,
          );
        } catch (e2) {
          if (e2 instanceof MediaUploadError) throw e2;
          throw err(ERROR_CODES.INTERNAL, 'Unexpected upload failure.', {
            cause: e2,
          });
        }
      } else if (e instanceof MediaUploadError) {
        throw e;
      } else {
        throw err(ERROR_CODES.INTERNAL, 'Unexpected upload failure.', {
          cause: e,
        });
      }
    }

    onProgress(100);

    const secureUrl = data.secure_url;
    const transformedUrl = transform
      ? buildTransformedUrl(secureUrl, isVideo || isGif)
      : secureUrl;
    const variants = applyFallbackVariants(secureUrl);

    if (enableHashDedupe && dedupe && partialHash) {
      await saveDedupeRecord({
        fingerprint,
        partialHash,
        secureUrl,
        variants,
        createdAt: Date.now(),
      });
    }

    const tokenOut = shouldChunk
      ? { uid: resumeRecord?.uid, token: resumeRecord?.token, fingerprint }
      : null;

    return {
      secureUrl,
      transformedUrl,
      canceled: false,
      deduped: false,
      imageMeta,
      videoMeta,
      thumbnails,
      variants,
      resumeToken: tokenOut?.token || null,
      raw: returnRaw ? data : undefined,
    };
  };

  const resumeUpload = (resumeToken, file, opts = {}) =>
    upload(file, { ...opts, resumeToken });

  return {
    validate,
    preflight,
    upload,
    resumeUpload,
    cancel,
    isCanceled: () => canceled,
    computeFingerprint,
    getCachedUpload,
    listResumables,
    purgeResumable,
    purgeAllResumables,
    config: {
      cloudName,
      uploadPreset,
      maxImageBytes,
      maxVideoBytes,
      chunkThresholdBytes,
      chunkSizeBytes,
      maxImageMegapixels,
      maxVideoDurationSec,
      maxVideoBitrateMbps,
      signed,
    },
    ERROR_CODES,
  };
}
